<template>
    <div class="alert-monitor-page" style="min-height: 90vh">
        <div v-if="isLoadingAttendanceEventButton || isLoadingEvents" class="loading-overlay">
            <i class="fas fa-spinner fa-spin loading-icon"></i>
        </div>

        <div class="monitor-sticky-header">
            <div class="monitor-sticky-inner">
                <h1 class="page-header mb-0 monitor-title">Ocorrências AlertPort</h1>
                <div class="alertport-kpis">
                    <div class="alertport-kpi">
                        <span class="alertport-kpi-label">Eventos ativos</span>
                        <span class="alertport-kpi-value">{{ totalEventsCount || events.length }}</span>
                    </div>
                    <div class="alertport-kpi">
                        <span class="alertport-kpi-label">SOS</span>
                        <span class="alertport-kpi-value text-danger">{{ sosCount }}</span>
                    </div>
                    <div class="alertport-kpi">
                        <span class="alertport-kpi-label">Presença (7d)</span>
                        <span class="alertport-kpi-value">{{ timeEntries.length }}</span>
                    </div>
                </div>
            </div>
        </div>

        <audio ref="remoteAudio" autoplay class="d-none"></audio>

        <transition name="call-modal-fade">
            <div v-if="callModalVisible" class="call-modal-backdrop">
                <div class="call-modal-shell">
                    <div class="call-modal-card" :class="{ 'is-ringing': isIncomingCallRinging }">
                        <div class="call-modal-glow"></div>
                        <div class="call-modal-header">
                            <div class="call-modal-avatar">
                                <i class="fa fa-shield-alt"></i>
                            </div>
                            <div class="call-modal-header-info">
                                <p class="call-modal-overline">{{ callModalOverline }}</p>
                                <h4 class="call-modal-name">{{ callPeerDisplayName }}</h4>
                                <p class="call-modal-peer-type">Dispositivo AlertPort</p>
                            </div>
                            <span class="call-modal-state" :class="{ ringing: isIncomingCallRinging, connected: isCallConnected }">
                                {{ callConnectionStateLabel }}
                            </span>
                        </div>

                        <div class="call-modal-body">
                            <div class="call-modal-timer">{{ formattedCallDuration }}</div>
                            <div class="call-modal-status">{{ callModalStatusText }}</div>
                        </div>

                        <div v-if="isIncomingCallRinging" class="call-modal-actions">
                            <button type="button" class="call-action-btn reject" :disabled="callActionBusy" @click="rejectIncomingCall">
                                <i class="fa fa-times"></i>
                                <span>Rejeitar</span>
                            </button>
                            <button type="button" class="call-action-btn accept" :disabled="callActionBusy" @click="acceptIncomingCall">
                                <i class="fa fa-phone"></i>
                                <span>Atender</span>
                            </button>
                        </div>

                        <div v-else class="call-modal-actions active-call">
                            <button
                                type="button"
                                class="call-action-btn control"
                                :class="{ active: callMicMuted || isSilentListenMicLocked }"
                                :disabled="isSilentListenMicLocked"
                                @click="toggleCallMicMute"
                            >
                                <i :class="isSilentListenMicLocked || callMicMuted ? 'fa fa-microphone-slash' : 'fa fa-microphone'"></i>
                                <span>{{ isSilentListenMicLocked ? 'Mic bloqueado (escuta)' : callMicMuted ? 'Ativar mic' : 'Mutar mic' }}</span>
                            </button>
                            <button type="button" class="call-action-btn control" :class="{ active: callRemoteMuted }" @click="toggleRemoteAudioMute">
                                <i :class="callRemoteMuted ? 'fa fa-volume-mute' : 'fa fa-volume-up'"></i>
                                <span>{{ callRemoteMuted ? 'Ativar áudio' : 'Silenciar' }}</span>
                            </button>
                            <button type="button" class="call-action-btn end" :disabled="callActionBusy" @click="endCall">
                                <i class="fa fa-phone"></i>
                                <span>Encerrar</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Filters -->
        <div class="row mt-2">
            <div class="col-md-12 mb-3">
                <div id="accordion" class="accordion rounded overflow-hidden">
                    <b-card class="bg-gray-800 text-white border-0 rounded-0" no-body>
                        <b-card-header
                            header-tag="header"
                            class="card-header bg-gray-900 text-white pointer-cursor px-3 py-10px d-flex align-items-center"
                            v-b-toggle.filterSection
                        >
                            <div class="filter-title">{{ $t('str.filters') }}</div>
                        </b-card-header>
                        <b-collapse id="filterSection" visible accordion="filterSession">
                            <b-card-body>
                                <div class="row">
                                    <div v-if="isSuperAdminMaster" class="col-md-3 mb-3">
                                        <label class="form-label">{{ $t('str.register.user.account.field') }}</label>
                                        <select v-model="filters.account" @change="changeAccount" class="form-select">
                                            <option value="">{{ $t('str.register.select.placeholder') }}</option>
                                            <option v-for="account in accounts" :value="account._id" :key="account._id">{{ account.name }}</option>
                                        </select>
                                    </div>
                                    <div v-if="isSuperAdminMaster || role === 'ADMIN' || role === 'MANAGER'" class="col-md-3 mb-3">
                                        <label class="form-label">{{ $t('str.register.user.client.field') }}</label>
                                        <select v-model="filters.client" @change="changeClient" class="form-select">
                                            <option value="">{{ $t('str.register.select.placeholder') }}</option>
                                            <option v-for="client in clients" :value="client._id" :key="client._id">{{ client.name }}</option>
                                        </select>
                                    </div>
                                    <div class="col-md-3 mb-3">
                                        <label class="form-label">{{ $t('str.register.user.site.field') }}</label>
                                        <select v-model="filters.site" @change="filter" class="form-select">
                                            <option value="">Todos</option>
                                            <option v-for="site in sites" :value="site._id" :key="site._id">{{ site.name }}</option>
                                        </select>
                                    </div>
                                </div>
                            </b-card-body>
                        </b-collapse>
                    </b-card>
                </div>
            </div>
        </div>

        <!-- Tabs -->
        <div class="row">
            <div class="col-md-12 mb-3">
                <div class="alertport-tabs">
                    <button
                        type="button"
                        class="alertport-tab"
                        :class="{ active: activeTab === 'events' }"
                        @click="activeTab = 'events'"
                    >
                        <i class="fa fa-bell me-2"></i>Eventos AlertPort
                        <span class="alertport-tab-counter">{{ totalEventsCount || events.length }}</span>
                    </button>
                    <button
                        type="button"
                        class="alertport-tab"
                        :class="{ active: activeTab === 'presence' }"
                        @click="activeTab = 'presence'"
                    >
                        <i class="fa fa-clock me-2"></i>Controle de Presença
                        <span class="alertport-tab-counter">{{ timeEntries.length }}</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Events tab -->
        <div v-if="activeTab === 'events'">
            <div v-if="events && events.length > 0" class="row">
                <!-- Event list -->
                <div class="col-xl-4 col-lg-6">
                    <panel title="Eventos AlertPort">
                        <div
                            ref="eventListContainer"
                            class="list-group list-group-flush rounded-bottom overflow-hidden panel-body p-0"
                            @scroll="onEventsListScroll"
                        >
                            <div
                                v-for="event in events"
                                :key="event?._id"
                                @click="handleSelectEvent(event)"
                            >
                                <a
                                    class="list-group-item list-group-item-action d-flex cursor-mouse"
                                    :class="selectedEvent?._id === event?._id ? 'list-group-selected' : ''"
                                >
                                    <div class="me-3 fs-16px">
                                        <i :class="event?.icon" />
                                    </div>
                                    <div class="flex-fill">
                                        <div class="fs-14px lh-12 mb-2px fw-bold">{{ event?.failureText }}</div>
                                        <div class="mb-1 fs-12px">
                                            <div class="flex-1">{{ event?.description }}</div>
                                        </div>
                                        <div class="mb-1">
                                            <span :class="event?.tag">{{ event?.failureText }}</span>
                                        </div>
                                    </div>
                                    <div v-if="!event?.attendance || !event?.attendance?.isAttendance" class="dot animated-dot">
                                        <i class="fa fa-circle dot-color" />
                                    </div>
                                </a>
                            </div>
                            <div v-if="isLoadingMoreEvents" class="text-center py-2">
                                <i class="fas fa-spinner fa-spin text-white"></i>
                            </div>
                            <div v-else-if="!allEventsLoaded && events.length > 0" class="text-center py-2">
                                <a class="cursor-mouse text-muted small" @click="loadMoreAlertportEvents">Carregar mais</a>
                            </div>
                        </div>
                    </panel>
                </div>

                <!-- Event details -->
                <div v-if="selectedEvent" class="col-xl-4 col-lg-6">
                    <panel :title="`${selectedEvent?.failureText} - ${selectedEvent?.formattedDate} ${selectedEvent?.formattedTime}`">
                        <div class="result-info">
                            <div class="flex-1 mb-3">
                                <div class="text-opacity-50 small fw-bold">
                                    Local:
                                    <label class="info-result">{{ selectedEvent?.site?.name || '-' }}</label>
                                </div>
                                <div class="text-opacity-50 small fw-bold">
                                    Cliente:
                                    <label class="info-result">{{ selectedEvent?.client?.name || '-' }}</label>
                                </div>
                                <div v-if="selectedEvent?.deviceInfo?.deviceId" class="text-opacity-50 small fw-bold">
                                    Dispositivo:
                                    <label class="info-result">{{ selectedEvent?.deviceInfo?.deviceId }}</label>
                                </div>
                                <div class="text-opacity-50 small fw-bold">
                                    Status do dispositivo:
                                    <span :class="['vigilant-connection-badge', isSelectedEventDeviceOnline ? 'is-online' : 'is-offline']">
                                        <i class="fa fa-circle"></i>
                                        {{ isSelectedEventDeviceOnline ? 'Online' : 'Offline' }}
                                    </span>
                                </div>
                            </div>
                            <hr class="bg-gray-500" />

                            <div v-if="selectedEvent?.attendance?.isAttendance" class="flex-1 mb-3">
                                <div class="text-opacity-50 small fw-bold">
                                    Atendido por:
                                    <label class="info-result">
                                        {{ selectedEvent?.attendance?.operator?.firstName }}
                                        {{ selectedEvent?.attendance?.operator?.lastName }}
                                    </label>
                                </div>
                                <div class="text-opacity-50 small fw-bold">
                                    Aberto em:
                                    <label class="info-result">{{ formatDate(selectedEvent?.attendance?.openedDate) }}</label>
                                </div>
                                <div v-if="selectedEvent?.attendance?.closedDate" class="text-opacity-50 small fw-bold">
                                    Fechado em:
                                    <label class="info-result">{{ formatDate(selectedEvent?.attendance?.closedDate) }}</label>
                                </div>
                                <hr class="bg-gray-500" />
                            </div>

                            <div
                                v-if="selectedEventCoordinates"
                                class="device-map-section mb-3"
                            >
                                <h6 class="fw-bold mb-2">Localização</h6>
                                <div class="device-map-wrapper">
                                    <iframe
                                        class="device-map-frame"
                                        loading="lazy"
                                        referrerpolicy="no-referrer-when-downgrade"
                                        :src="`https://maps.google.com/maps?q=${selectedEventCoordinates.latitude},${selectedEventCoordinates.longitude}&z=16&output=embed`"
                                    />
                                    <small class="text-muted d-block mt-2">
                                        Lat: {{ selectedEventCoordinates.latitude }} | Lng: {{ selectedEventCoordinates.longitude }}
                                    </small>
                                </div>
                            </div>

                            <div class="call-actions-section mb-3">
                                <h6 class="fw-bold mb-3">Comunicação em tempo real</h6>
                                <div class="call-actions-grid">
                                    <button
                                        type="button"
                                        class="btn btn-primary"
                                        :disabled="isStartingCall || !canCallSelectedEventDevice"
                                        @click="callSelectedEventDevice('NORMAL')"
                                    >
                                        <i class="fa fa-phone me-2"></i>
                                        {{ isStartingCall ? 'Iniciando...' : 'Ligar para dispositivo' }}
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-warning"
                                        :disabled="isStartingCall || !canCallSelectedEventDevice"
                                        @click="callSelectedEventDevice('SILENT_LISTEN')"
                                    >
                                        <i class="fa fa-headphones me-2"></i>
                                        {{ isStartingCall ? 'Iniciando...' : 'Escuta ativa' }}
                                    </button>
                                </div>
                                <div v-if="callStatus" class="call-status-text mt-2">
                                    <small class="text-muted">{{ callStatus }}</small>
                                </div>
                            </div>

                            <div v-if="canStartSelectedEventAttendance" class="result-price">
                                <a
                                    @click="handleAttendanceEvent()"
                                    class="btn d-block w-100 btn-danger"
                                >
                                    <i v-if="isLoadingAttendanceEventButton" class="fas fa-spinner fa-spin"></i>
                                    Iniciar atendimento
                                </a>
                            </div>
                        </div>
                    </panel>
                </div>

                <!-- Attendance panel -->
                <div class="col-xl-4 col-lg-6 panel-attendance-body p-0" v-if="selectedEvent?.attendance?.isAttendance">
                    <panel title="Atendimento do evento">
                        <div
                            v-if="isSelectedEventAttendanceOwner && selectedEvent?.attendance?.status === 'IN_PROGRESS'"
                            class="result-info overflow-hidden"
                        >
                            <div class="flex-1 mb-3">
                                <label class="form-label" for="attendancesOptionsField">{{ $t('str.attendances.options.field') }}</label>
                                <select
                                    v-model="attendance.type"
                                    class="form-select"
                                    id="attendancesOptionsField"
                                    @focus="removeRequiredField('attendanceOptions')"
                                    :class="checkRequiredField('attendanceOptions') ? 'is-invalid' : ''"
                                >
                                    <option value="">{{ $t('str.register.select.placeholder') }}</option>
                                    <option v-for="option in attendancesTypes" :value="option._id" :key="option._id">
                                        {{ $t(option._id) }}
                                    </option>
                                </select>
                                <div class="invalid-feedback">{{ $t('str.attendances.options.field.error') }}</div>
                            </div>
                            <div class="flex-1 mb-3">
                                <label class="form-label" for="attendancesNotesField">{{ $t('str.attendances.notes.field') }}</label>
                                <textarea v-model="attendance.notes" class="form-control" id="attendancesNotesField" rows="3"></textarea>
                            </div>

                            <div class="result-price d-flex justify-content-between align-items-center mb-3">
                                <a @click="checkForm()" class="btn d-block w-50 m-2 btn-primary">
                                    <i v-if="isLoadingSendAttendanceButton" class="fas fa-spinner fa-spin"></i>
                                    {{ $t('str.btn.attendance.register') }}
                                </a>
                                <a @click="confirmCloseAttendance" class="btn d-block w-50 m-2 btn-gray">
                                    {{ $t('str.btn.attendance.close') }}
                                </a>
                            </div>
                            <hr class="bg-gray-500" />
                        </div>

                        <div class="result-info pd-0">
                            <label class="form-label">{{ $t('str.attendances.history.list') }}</label>
                            <table class="table table-hover table-striped table-bordered">
                                <thead>
                                    <tr>
                                        <th scope="col">{{ $t('str.attendances.date.field') }}</th>
                                        <th scope="col">{{ $t('str.attendances.type.field') }}</th>
                                        <th width="30%" scope="col">{{ $t('str.attendances.notes.field') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="isLoadingAttendanceList">
                                        <td colspan="3" class="text-center">
                                            <i class="fas fa-spinner fa-spin"></i>
                                        </td>
                                    </tr>
                                    <tr v-else-if="!isLoadingAttendanceList && attendances.length === 0">
                                        <td colspan="3" class="text-center">{{ $t('str.msg.waiting.attendance.by.operator') }}</td>
                                    </tr>
                                    <tr v-else v-for="attendanceItem in attendances" :key="attendanceItem._id">
                                        <td>{{ formatDate(attendanceItem.createDate) }}</td>
                                        <td>{{ $t(attendanceItem.type) }}</td>
                                        <td>{{ attendanceItem.notes }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </panel>
                </div>
            </div>

            <div v-else class="no-events">
                <div>
                    <i class="fa fa-shield-alt fa-5x text-white mb-4"></i>
                    <h3 class="info-title">Nenhuma ocorrência AlertPort</h3>
                    <p>Aguardando novos eventos enviados pelos dispositivos.</p>
                </div>
            </div>
        </div>

        <!-- Presence tab -->
        <div v-else-if="activeTab === 'presence'">
            <div class="row">
                <div class="col-md-12">
                    <panel title="Controle de Presença - AlertPort">
                        <div v-if="isLoadingTimeEntries" class="text-center p-4">
                            <i class="fas fa-spinner fa-spin"></i>
                        </div>
                        <div v-else-if="timeEntries.length === 0" class="text-center text-muted p-4">
                            Nenhum registro de presença nos últimos 7 dias
                        </div>
                        <div v-else class="table-responsive">
                            <table class="table table-hover table-striped">
                                <thead>
                                    <tr>
                                        <th>Data/Hora</th>
                                        <th>Funcionário</th>
                                        <th>Tipo</th>
                                        <th>Método</th>
                                        <th>Local</th>
                                        <th>Cliente</th>
                                        <th>Equipamento</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="entry in timeEntries" :key="entry._id">
                                        <td>{{ formatDate(entry.createdAt) }}</td>
                                        <td>
                                            <strong>{{ entry?.user?.fullName || [entry?.user?.firstName, entry?.user?.lastName].filter(Boolean).join(' ') || '-' }}</strong>
                                        </td>
                                        <td>
                                            <span class="badge bg-info text-white time-entry-badge">{{ formatTimeEntryEventType(entry.eventType) }}</span>
                                        </td>
                                        <td>{{ entry.method || '-' }}</td>
                                        <td>{{ entry?.site?.name || '-' }}</td>
                                        <td>{{ entry?.client?.name || '-' }}</td>
                                        <td>{{ entry?.equipment?.name || '-' }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </panel>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from 'vue'
import Controller from './CrtAlertMonitor.vue'
import socket from '../../../config/websocket.js'
import { clearPendingIncomingCall, consumePendingIncomingCall, subscribePendingIncomingCall } from '../../../services/operatorCallBridge'
import Services from '../../../common/Services.vue'
import { db } from '../../../firebaseInit.js'
import { deleteDoc, doc, onSnapshot } from 'firebase/firestore'
import phoneRingtoneUrl from '../../../assets/sound/phone-ringtone.wav'
import alarmSoundUrl from '../../../assets/sound/alarm.mp3'
import {
    attachStreamToRecording,
    createCallRecordingState,
    resetCallRecordingState,
    setCallRecordingPolicy,
    startCallRecording,
    stopCallRecordingAndUpload,
} from '../../../services/callRecordingBridge'
import { getWebRtcIceServers } from '../../../services/webrtcIceConfig'

Vue.prototype.$registerEvent = new Vue()

export default {
    data() {
        return {
            user: null,
            role: null,
            isSuperAdminMaster: false,
            domain: '',
            companySettings: null,
            accounts: [],
            clients: [],
            sites: [],
            filters: { account: '', client: '', site: '' },

            // Events / data
            items: [],
            events: [],
            timeEntries: [],
            selectedEvent: null,
            totalEventsCount: 0,
            monitorPage: 1,
            allEventsLoaded: false,
            isLoadingEvents: false,
            isLoadingMoreEvents: false,
            isLoadingTimeEntries: false,
            activeTab: 'events',

            // Attendance
            attendancesTypes: [],
            attendances: [],
            attendance: { type: '', notes: '' },
            attendanceEventEnabled: false,
            isLoadingAttendanceEventButton: false,
            isLoadingSendAttendanceButton: false,
            isLoadingAttendanceList: false,
            errors: [],

            // Firestore subscribers
            subscribes: [],
            notificationSoundAudio: null,

            // Socket/Call state
            socketConnected: false,
            socketUserId: '',
            onlineUsers: [],
            callTargetUserId: '',
            callStatus: '',
            activeCallRoomId: '',
            pendingIncomingCall: null,
            incomingCallAccepted: false,
            callDuration: 0,
            callPc: null,
            callLocalStream: null,
            callPendingIce: [],
            callPendingOffer: null,
            incomingRingtoneAudio: null,
            socketHandlers: null,
            callPeerUserId: '',
            callPeerLabel: '',
            callPeerClientType: '',
            callPeerRole: '',
            callMicMuted: false,
            callRemoteMuted: false,
            callActionBusy: false,
            callMode: 'NORMAL',
            isStartingCall: false,
            callRecordingState: createCallRecordingState(),
            operatorIncomingCallBridgeUnsubscribe: null,
        }
    },

    computed: {
        sosCount() {
            return (this.events || []).filter(event => event?.type === 'SOS_ALERT').length
        },
        selectedEventDeviceUserId() {
            return String(this.selectedEvent?.deviceInfo?.deviceId || '')
        },
        isSelectedEventDeviceOnline() {
            const deviceId = this.selectedEventDeviceUserId
            if (!deviceId) return false
            return (this.onlineUsers || []).some(userId => String(userId) === deviceId)
        },
        selectedEventCoordinates() {
            const latitudeRaw = this.selectedEvent?.geolocation?.latitude ?? this.selectedEvent?.deviceInfo?.geolocation?.latitude
            const longitudeRaw = this.selectedEvent?.geolocation?.longitude ?? this.selectedEvent?.deviceInfo?.geolocation?.longitude
            const latitude = Number(latitudeRaw)
            const longitude = Number(longitudeRaw)
            if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
            return { latitude, longitude }
        },
        canCallSelectedEventDevice() {
            return Boolean(
                this.socketConnected &&
                    this.user?._id &&
                    this.selectedEventDeviceUserId &&
                    !this.activeCallRoomId &&
                    !this.pendingIncomingCall,
            )
        },
        canStartSelectedEventAttendance() {
            return Boolean(
                this.selectedEvent &&
                    (!this.selectedEvent?.attendance || !this.selectedEvent?.attendance?.isAttendance) &&
                    this.user?.companyUser?.subtype === 'OPERATOR',
            )
        },
        selectedAttendanceOperatorId() {
            const operator = this.selectedEvent?.attendance?.operator
            if (!operator) return ''
            if (typeof operator === 'string') return operator
            return operator?._id || operator?.id || ''
        },
        isSelectedEventAttendanceOwner() {
            return Boolean(
                this.selectedEvent?.attendance?.isAttendance &&
                    this.selectedAttendanceOperatorId &&
                    this.selectedAttendanceOperatorId === this.user?._id,
            )
        },
        callModalVisible() {
            return Boolean(this.pendingIncomingCall || this.activeCallRoomId)
        },
        isIncomingCallRinging() {
            return Boolean(this.pendingIncomingCall && !this.incomingCallAccepted)
        },
        isCallConnected() {
            return Boolean(this.activeCallRoomId && this.incomingCallAccepted)
        },
        formattedCallDuration() {
            const s = Number.isFinite(this.callDuration) ? Math.max(0, Math.floor(this.callDuration)) : 0
            return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
        },
        callPeerDisplayName() {
            return this.normalizeCallPeerLabel(this.callPeerLabel || this.pendingIncomingCall?.fromLabel || this.callPeerUserId || '')
        },
        callConnectionStateLabel() {
            if (this.callMode === 'SILENT_LISTEN') return 'Escuta ativa'
            if (this.isIncomingCallRinging) return 'Chamando'
            if (this.isCallConnected) return 'Em ligação'
            return 'Conectando'
        },
        callModalOverline() {
            if (this.callMode === 'SILENT_LISTEN') return 'Escuta ativa'
            if (this.isIncomingCallRinging) return 'Chamada recebida'
            return 'Chamada em andamento'
        },
        callModalStatusText() {
            if (this.callStatus) return this.callStatus
            if (this.isIncomingCallRinging) return 'Aguardando sua decisão'
            if (this.isCallConnected) return 'Conexão ativa com áudio'
            return 'Conectando chamada...'
        },
        isSilentListenMicLocked() {
            return this.callMode === 'SILENT_LISTEN'
        },
    },

    watch: {
        'filters.account'(next, prev) {
            if (!next || next === prev) return
            if (this.socketConnected) this.registerOnSocket()
        },
    },

    methods: {
        ...Controller.methods,

        onEventsListScroll() {
            const container = this.$refs.eventListContainer
            if (!container) return
            const nearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 50
            if (nearBottom && !this.isLoadingMoreEvents && !this.allEventsLoaded) {
                this.loadMoreAlertportEvents()
            }
        },

        isAlertportClientType(clientType) {
            return String(clientType || '').toUpperCase().includes('ALERTPORT')
        },

        applyCallRecordingPolicy(payloadPolicy) {
            const enabled = typeof payloadPolicy === 'boolean' ? payloadPolicy : this.companySettings?.monitor?.callRecordingEnabled === true
            setCallRecordingPolicy(this.callRecordingState, enabled)
        },

        async loadCompanySettingsForRecording() {
            try {
                const settings = await Services.getMyCompanySettings(this)
                this.companySettings = settings || {}
            } catch (_error) {
                this.companySettings = {}
            }
            this.applyCallRecordingPolicy(null)
        },

        playAlertNotificationSound() {
            try {
                if (!this.notificationSoundAudio) {
                    this.notificationSoundAudio = new Audio(alarmSoundUrl)
                    this.notificationSoundAudio.preload = 'auto'
                }
                this.notificationSoundAudio.currentTime = 0
                const p = this.notificationSoundAudio.play()
                if (p?.catch) p.catch(() => {})
            } catch (_error) {
                /* noop */
            }
        },

        setupAlertRealtimeListeners() {
            this.subscribes.forEach(unsubscribe => unsubscribe())
            this.subscribes = []

            const siteIds = (Array.isArray(this.sites) ? this.sites : []).map(site => String(site?._id || '')).filter(Boolean)
            siteIds.forEach(siteId => {
                const notificationDocRef = doc(db, 'notifications', siteId)
                const unsubscribeNotifications = onSnapshot(notificationDocRef, async snapshot => {
                    if (!snapshot.exists()) return
                    const data = snapshot.data()
                    if (!data) return
                    // Atenção apenas a notificações originadas do alertport-app.
                    if (String(data?.source || '').toUpperCase() !== 'ALERTPORT') return

                    const type = data?.type
                    if (type === 'SOS_ALERT' || type === 'INCIDENT' || type === 'CRASH' || type === 'LOWVOLTAGE' || type === 'FAILURE_PATROL' || type === 'CANCEL_PATROL') {
                        this.playAlertNotificationSound()
                        await this.loadAlertportEvents(1, { isFirstPage: true })
                    } else if (type === 'TIME_ENTRY') {
                        await this.loadAlertportTimeEntries()
                    }

                    try {
                        await deleteDoc(notificationDocRef)
                    } catch {
                        /* ignore */
                    }
                })
                this.subscribes.push(unsubscribeNotifications)
            })
        },

        // --- Socket / Call methods ---

        initCallSocket() {
            if (this.socketHandlers) return

            const onConnect = () => {
                this.socketConnected = true
                this.registerOnSocket()
            }
            const onDisconnect = () => {
                this.socketConnected = false
                this.stopIncomingRingtone()
                if (this.activeCallRoomId || this.pendingIncomingCall) {
                    this.callStatus = 'Conexão com o chat foi perdida'
                    this.cleanupCall()
                }
            }
            const onUserList = ({ users }) => {
                this.onlineUsers = Array.isArray(users) ? users : []
            }
            const onCallTick = ({ roomId, elapsed }) => {
                if (this.activeCallRoomId && roomId === this.activeCallRoomId) this.callDuration = elapsed
            }
            const onCallAccept = ({ roomId, userId }) => {
                if (this.activeCallRoomId && roomId === this.activeCallRoomId) {
                    this.stopIncomingRingtone()
                    this.callStatus = 'Chamada em andamento'
                    if (!this.callPeerUserId && userId) this.callPeerUserId = userId
                }
            }
            const onCallIncoming = ({ roomId, from, fromLabel, fromClientType, fromRole, fromDisplayName, callMode }) => {
                // Somente aceita ligações recebidas de dispositivos alertport-app.
                // Chamadas do shieldgo-app continuam sendo tratadas pela tela de vigilância.
                if (!this.isAlertportClientType(fromClientType)) return
                this.applyIncomingCallPayload({ roomId, from, fromLabel, fromClientType, fromRole, fromDisplayName, callMode })
            }
            const onCallReject = ({ roomId }) => {
                if (this.activeCallRoomId && roomId === this.activeCallRoomId) {
                    this.stopIncomingRingtone()
                    this.callStatus = 'Chamada rejeitada'
                    clearPendingIncomingCall(roomId)
                    this.cleanupCall()
                }
            }
            const onCallEnd = ({ roomId, reason, duration }) => {
                if (this.activeCallRoomId && roomId === this.activeCallRoomId) {
                    this.stopIncomingRingtone()
                    this.callStatus = reason ? `Encerrada (${reason})` : `Encerrada (${duration ?? 0}s)`
                    clearPendingIncomingCall(roomId)
                    this.cleanupCall()
                }
            }
            const onAnswer = async ({ roomId, sdp }) => {
                if (!this.callPc || !this.activeCallRoomId || roomId !== this.activeCallRoomId) return
                try {
                    const SessionDesc = window.RTCSessionDescription || window.webkitRTCSessionDescription
                    const desc = SessionDesc ? new SessionDesc(sdp) : sdp
                    await this.callPc.setRemoteDescription(desc)
                    this.callStatus = 'Conectando áudio...'
                } catch (e) {
                    this.callStatus = `Erro ao aplicar answer: ${String(e?.message || e)}`
                }
            }
            const onOffer = async ({ roomId, from, sdp }) => {
                if (!this.activeCallRoomId || roomId !== this.activeCallRoomId) return
                if (this.pendingIncomingCall && !this.incomingCallAccepted) {
                    this.callPendingOffer = { roomId, from, sdp }
                    return
                }
                await this.processOffer({ roomId, from, sdp })
            }
            const onIce = async ({ roomId, candidate }) => {
                if (!this.activeCallRoomId || roomId !== this.activeCallRoomId) return
                if (!this.callPc) {
                    this.callPendingIce.push(candidate)
                    return
                }
                try {
                    const IceCand = window.RTCIceCandidate || window.webkitRTCIceCandidate
                    const ice = IceCand ? new IceCand(candidate) : candidate
                    await this.callPc.addIceCandidate(ice)
                } catch (e) {
                    /* ignore */
                }
            }

            this.socketHandlers = {
                onConnect,
                onDisconnect,
                onUserList,
                onCallTick,
                onCallAccept,
                onCallIncoming,
                onCallReject,
                onCallEnd,
                onOffer,
                onAnswer,
                onIce,
            }

            socket.on('connect', onConnect)
            socket.on('disconnect', onDisconnect)
            socket.on('user:list', onUserList)
            socket.on('call:duration:tick', onCallTick)
            socket.on('call:accept', onCallAccept)
            socket.on('call:incoming', onCallIncoming)
            socket.on('call:reject', onCallReject)
            socket.on('call:end', onCallEnd)
            socket.on('webrtc:offer', onOffer)
            socket.on('webrtc:answer', onAnswer)
            socket.on('webrtc:ice', onIce)

            this.socketConnected = socket.connected
            if (socket.connected) this.registerOnSocket()
        },

        registerOnSocket() {
            const userId = this.getSocketUserId()
            if (!userId) return

            const accountId = this.getSocketAccountId()
            const role = this.getSocketRole()
            if (!accountId) {
                this.callStatus = 'Conta não identificada para registrar no ms-chat.'
                return
            }

            this.socketUserId = userId
            if (!socket.connected) return

            const displayName = this.getSocketDisplayName()
            if (role === 'OPERATOR' && !this.filters?.account) {
                this.filters.account = accountId
            }

            socket.emit('user:register', { userId, accountId, role, clientType: 'ADMIN_MONITOR', displayName }, ack => {
                if (ack?.ok) {
                    this.callStatus = 'Registrado no ms-chat'
                } else if (ack?.error) {
                    this.callStatus = `Falha ao registrar: ${ack.error}`
                }
            })
        },

        normalizeCallPeerLabel(value) {
            const text = value ? String(value).trim() : ''
            if (!text) return 'Dispositivo AlertPort'
            if (/^[a-fA-F0-9]{24}$/.test(text)) return 'Dispositivo AlertPort'
            return text
        },

        applyIncomingCallPayload(payload = {}) {
            const roomId = payload?.roomId || ''
            const from = payload?.from || ''
            const fromClientType = payload?.fromClientType || ''
            if (!this.isAlertportClientType(fromClientType)) return

            const callMode = payload?.callMode === 'SILENT_LISTEN' ? 'SILENT_LISTEN' : 'NORMAL'
            this.applyCallRecordingPolicy(payload?.callRecordingEnabled)

            if (!roomId || !from) return

            if (this.activeCallRoomId || this.pendingIncomingCall) {
                const currentRoomId = this.activeCallRoomId || this.pendingIncomingCall?.roomId || ''
                if (String(currentRoomId) === String(roomId)) return
                socket.emit('call:reject', { roomId, userId: this.user?._id })
                return
            }

            const resolvedLabel = this.normalizeCallPeerLabel(payload?.fromLabel || payload?.fromDisplayName || from)
            this.activeCallRoomId = roomId
            this.callTargetUserId = from
            this.callMode = callMode
            this.callDuration = 0
            this.incomingCallAccepted = false
            this.callPendingOffer = null
            this.callMicMuted = callMode === 'SILENT_LISTEN'
            this.callPeerUserId = from
            this.callPeerLabel = resolvedLabel
            this.callPeerClientType = fromClientType
            this.callPeerRole = payload?.fromRole || ''
            this.pendingIncomingCall = { roomId, from, fromLabel: resolvedLabel }
            this.callStatus = `Ligação recebida de ${resolvedLabel}`
            this.startIncomingRingtone()
        },

        consumeIncomingCallFromBridge() {
            const pendingIncoming = consumePendingIncomingCall()
            if (!pendingIncoming?.roomId) return
            if (!this.isAlertportClientType(pendingIncoming?.fromClientType)) return
            this.applyIncomingCallPayload(pendingIncoming)
        },

        async callSelectedEventDevice(mode = 'NORMAL') {
            if (!this.selectedEvent) return
            const deviceUserId = this.selectedEventDeviceUserId
            if (!deviceUserId) {
                this.callStatus = 'Dispositivo sem ID'
                return
            }

            this.isStartingCall = true
            try {
                const label = this.selectedEvent?.site?.name || 'Dispositivo AlertPort'
                const result = await this.startCall({
                    to: deviceUserId,
                    toLabel: label,
                    peerClientType: 'ALERTPORT_DEVICE',
                    callMode: mode,
                })
                if (!result?.ok && result?.error) {
                    this.callStatus = `Falha: ${result.error}`
                }
            } finally {
                this.isStartingCall = false
            }
        },

        async startCall(options = {}) {
            const targetUserId = options?.to || this.callTargetUserId
            if (!targetUserId) return { ok: false, error: 'MISSING_TARGET' }
            if (!socket.connected) {
                this.callStatus = 'Socket desconectado'
                return { ok: false, error: 'SOCKET_DISCONNECTED' }
            }

            const callMode = options?.callMode || 'NORMAL'
            this.callMode = callMode
            this.callMicMuted = callMode === 'SILENT_LISTEN'
            this.callStatus = callMode === 'SILENT_LISTEN' ? 'Iniciando escuta ativa...' : 'Iniciando chamada...'
            this.callDuration = 0

            const to = targetUserId
            const from = this.user?._id
            const accountId = this.getSocketAccountId()
            const peerLabel = this.normalizeCallPeerLabel(options?.toLabel || '')

            return new Promise(resolve => {
                socket.emit('call:start', { to, from, accountId, callMode }, async ack => {
                    if (!ack?.ok) {
                        this.callStatus = `Falha ao iniciar: ${ack?.error || 'UNKNOWN'}`
                        resolve({ ok: false, error: ack?.error || 'UNKNOWN' })
                        return
                    }

                    this.activeCallRoomId = ack.roomId
                    this.incomingCallAccepted = true
                    this.pendingIncomingCall = null
                    this.callPendingOffer = null
                    if (ack?.to) this.callTargetUserId = ack.to
                    this.callPeerUserId = ack?.to || targetUserId
                    this.callPeerLabel = this.normalizeCallPeerLabel(ack?.toLabel || peerLabel || targetUserId)
                    this.callPeerClientType = options?.peerClientType || ''
                    this.applyCallRecordingPolicy(ack?.callRecordingEnabled)
                    this.callStatus = callMode === 'SILENT_LISTEN' ? `Escuta ativa: ${this.callPeerLabel}...` : `Chamando ${this.callPeerLabel}...`
                    if (ack?.targetOnline === false) {
                        this.callStatus = ack?.wakeupTriggered
                            ? 'Dispositivo offline. Push de wake-up enviado e chamada pendente.'
                            : 'Dispositivo offline. Chamada pendente aguardando reconexão.'
                    }

                    try {
                        await this.ensurePeerConnection()
                        const offer = await this.callPc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: false })
                        await this.callPc.setLocalDescription(offer)
                        socket.emit('webrtc:offer', { roomId: this.activeCallRoomId, to, sdp: offer }, offerAck => {
                            if (offerAck?.error) {
                                this.callStatus = `Falha offer: ${offerAck.error}`
                                return
                            }
                            if (offerAck?.queued) this.callStatus = 'Dispositivo em reconexão. Chamada pendente de entrega...'
                        })

                        const queued = this.callPendingIce
                        this.callPendingIce = []
                        for (const c of queued) {
                            const IceCand = window.RTCIceCandidate || window.webkitRTCIceCandidate
                            const ice = IceCand ? new IceCand(c) : c
                            await this.callPc.addIceCandidate(ice)
                        }
                        resolve({ ok: true })
                    } catch (e) {
                        this.callStatus = `Erro WebRTC: ${String(e?.message || e)}`
                        this.endCall()
                        resolve({ ok: false, error: String(e?.message || e) })
                    }
                })
            })
        },

        async acceptIncomingCall() {
            const pending = this.pendingIncomingCall
            if (!pending || this.callActionBusy) return
            if (!socket.connected) {
                this.callStatus = 'Socket desconectado'
                return
            }

            this.callActionBusy = true
            this.stopIncomingRingtone()
            socket.emit('call:accept', { roomId: pending.roomId, userId: this.user?._id }, async ack => {
                if (ack?.error) {
                    this.callStatus = `Falha ao aceitar: ${ack.error}`
                    this.cleanupCall()
                    this.callActionBusy = false
                    return
                }
                this.incomingCallAccepted = true
                this.pendingIncomingCall = null
                this.callStatus = 'Conectando áudio...'
                clearPendingIncomingCall(pending.roomId)

                const pendingOffer = this.callPendingOffer
                this.callPendingOffer = null
                if (pendingOffer && pendingOffer.roomId === pending.roomId) {
                    await this.processOffer(pendingOffer)
                }
                this.callActionBusy = false
            })
        },

        rejectIncomingCall() {
            const pending = this.pendingIncomingCall
            if (!pending || this.callActionBusy) return
            this.callActionBusy = true
            this.stopIncomingRingtone()
            clearPendingIncomingCall(pending.roomId)
            socket.emit('call:reject', { roomId: pending.roomId, userId: this.user?._id }, () => {
                this.cleanupCall()
                this.callActionBusy = false
            })
        },

        endCall() {
            const roomId = this.activeCallRoomId
            if (!roomId || this.callActionBusy) return
            this.callActionBusy = true
            socket.emit('call:end', { roomId, userId: this.user?._id }, () => {
                this.cleanupCall()
                this.callActionBusy = false
            })
        },

        async processOffer({ roomId, from, sdp }) {
            if (!this.activeCallRoomId || roomId !== this.activeCallRoomId) return
            if (!this.callTargetUserId) this.callTargetUserId = from
            if (!this.callPeerUserId && from) this.callPeerUserId = from

            try {
                await this.ensurePeerConnection()
                const SessionDesc = window.RTCSessionDescription || window.webkitRTCSessionDescription
                const remoteOffer = SessionDesc ? new SessionDesc(sdp) : sdp
                await this.callPc.setRemoteDescription(remoteOffer)

                const answer = await this.callPc.createAnswer({ offerToReceiveAudio: true, offerToReceiveVideo: false })
                await this.callPc.setLocalDescription(answer)

                socket.emit('webrtc:answer', { roomId: this.activeCallRoomId, to: this.callTargetUserId, sdp: answer }, ack => {
                    if (ack?.error) this.callStatus = `Falha answer: ${ack.error}`
                })
                this.callStatus = 'Conectando áudio...'

                const queued = this.callPendingIce
                this.callPendingIce = []
                for (const c of queued) {
                    const IceCand = window.RTCIceCandidate || window.webkitRTCIceCandidate
                    const ice = IceCand ? new IceCand(c) : c
                    await this.callPc.addIceCandidate(ice)
                }
            } catch (e) {
                this.callStatus = `Erro offer: ${String(e?.message || e)}`
                this.endCall()
            }
        },

        async ensurePeerConnection() {
            if (this.callPc) return
            const Pc = window.RTCPeerConnection || window.webkitRTCPeerConnection
            if (!Pc) throw new Error('RTCPeerConnection não disponível')

            this.callPc = new Pc({ iceServers: getWebRtcIceServers() })

            this.callPc.onicecandidate = ev => {
                if (!ev.candidate || !this.activeCallRoomId || !this.callTargetUserId) return
                const candidate = typeof ev.candidate?.toJSON === 'function' ? ev.candidate.toJSON() : ev.candidate
                socket.emit('webrtc:ice', { roomId: this.activeCallRoomId, to: this.callTargetUserId, candidate })
            }

            this.callPc.ontrack = ev => {
                let stream = (ev.streams && ev.streams[0]) || null
                if (!stream && ev.track && window.MediaStream) stream = new window.MediaStream([ev.track])
                if (!stream) return
                const audio = this.$refs.remoteAudio
                if (audio && audio.srcObject !== stream) {
                    audio.srcObject = stream
                    if (audio.play) audio.play().catch(() => null)
                }
                attachStreamToRecording(this.callRecordingState, stream, 'remote')
                if (this.callLocalStream) {
                    attachStreamToRecording(this.callRecordingState, this.callLocalStream, 'local')
                }
                startCallRecording(this.callRecordingState)
                this.callStatus = 'Áudio remoto conectado'
            }

            if (navigator?.mediaDevices?.getUserMedia) {
                this.callLocalStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
                this.callLocalStream.getTracks().forEach(track => {
                    this.callPc.addTrack(track, this.callLocalStream)
                })
                attachStreamToRecording(this.callRecordingState, this.callLocalStream, 'local')
                if (this.callLocalStream?.getAudioTracks) {
                    this.callLocalStream.getAudioTracks().forEach(track => {
                        track.enabled = !(this.callMicMuted || this.isSilentListenMicLocked)
                    })
                }
            } else {
                throw new Error('getUserMedia não disponível')
            }
        },

        async cleanupCall() {
            const currentRoomId = this.activeCallRoomId || this.pendingIncomingCall?.roomId || ''
            if (currentRoomId) clearPendingIncomingCall(currentRoomId)
            this.stopIncomingRingtone()
            if (this.callRecordingState?.mediaRecorder) {
                if (socket?.connected && currentRoomId) {
                    const uploadResult = await stopCallRecordingAndUpload(this.callRecordingState, socket, {
                        roomId: currentRoomId,
                        accountId: this.getSocketAccountId(),
                        callMode: this.callMode,
                        initiatedBy: this.user?._id || this.socketUserId || '',
                        peerUserId: this.callPeerUserId || this.callTargetUserId || '',
                        codec: 'opus',
                    })
                    if (!uploadResult?.ok && uploadResult?.error && uploadResult?.error !== 'NO_ACTIVE_RECORDING') {
                        this.callStatus = `Gravação não enviada: ${uploadResult.error}`
                    }
                } else {
                    resetCallRecordingState(this.callRecordingState)
                }
            } else {
                resetCallRecordingState(this.callRecordingState)
            }
            try {
                if (this.callPc) {
                    this.callPc.onicecandidate = null
                    this.callPc.ontrack = null
                    this.callPc.close()
                }
            } catch (e) {
                /* */
            }
            try {
                if (this.callLocalStream?.getTracks) this.callLocalStream.getTracks().forEach(t => t.stop())
            } catch (e) {
                /* */
            }
            try {
                const audio = this.$refs.remoteAudio
                if (audio) {
                    audio.srcObject = null
                    audio.muted = false
                }
            } catch (e) {
                /* */
            }

            this.callPc = null
            this.callLocalStream = null
            this.callPendingIce = []
            this.callPendingOffer = null
            this.activeCallRoomId = ''
            this.pendingIncomingCall = null
            this.incomingCallAccepted = false
            this.callDuration = 0
            this.callPeerUserId = ''
            this.callPeerLabel = ''
            this.callPeerClientType = ''
            this.callPeerRole = ''
            this.callMicMuted = false
            this.callRemoteMuted = false
            this.callActionBusy = false
            this.callMode = 'NORMAL'
            this.applyCallRecordingPolicy(null)
        },

        toggleCallMicMute() {
            if (this.isSilentListenMicLocked) return
            this.callMicMuted = !this.callMicMuted
            if (this.callLocalStream?.getAudioTracks) {
                this.callLocalStream.getAudioTracks().forEach(track => {
                    track.enabled = !this.callMicMuted
                })
            }
        },

        toggleRemoteAudioMute() {
            this.callRemoteMuted = !this.callRemoteMuted
            const audio = this.$refs.remoteAudio
            if (audio) audio.muted = this.callRemoteMuted
        },

        startIncomingRingtone() {
            try {
                if (!this.incomingRingtoneAudio) {
                    this.incomingRingtoneAudio = new Audio(phoneRingtoneUrl)
                    this.incomingRingtoneAudio.loop = true
                    this.incomingRingtoneAudio.preload = 'auto'
                }
                this.incomingRingtoneAudio.volume = 1
                this.incomingRingtoneAudio.currentTime = 0
                const p = this.incomingRingtoneAudio.play()
                if (p?.catch) p.catch(() => {})
            } catch (e) {
                /* */
            }
        },

        stopIncomingRingtone() {
            try {
                if (this.incomingRingtoneAudio) {
                    this.incomingRingtoneAudio.pause()
                    this.incomingRingtoneAudio.currentTime = 0
                }
            } catch (e) {
                /* */
            }
        },
    },

    async created() {
        await Controller.init(this)
        await this.loadCompanySettingsForRecording()
        await this.refreshAlertportData()
        if (this.isSuperAdminMaster) await this.loadAccounts()
        await this.loadClients()
        await this.loadSites()
        this.setupAlertRealtimeListeners()
        this.initCallSocket()
        this.$nextTick(() => {
            this.registerOnSocket()
            this.consumeIncomingCallFromBridge()
        })
        this.operatorIncomingCallBridgeUnsubscribe = subscribePendingIncomingCall(payload => {
            if (!payload?.roomId) return
            if (!this.isAlertportClientType(payload?.fromClientType)) return
            this.applyIncomingCallPayload(payload)
        })
    },

    beforeDestroy() {
        this.cleanupCall()
        if (typeof this.operatorIncomingCallBridgeUnsubscribe === 'function') {
            this.operatorIncomingCallBridgeUnsubscribe()
            this.operatorIncomingCallBridgeUnsubscribe = null
        }
        if (this.incomingRingtoneAudio) {
            try {
                this.incomingRingtoneAudio.pause()
                this.incomingRingtoneAudio.src = ''
            } catch (e) {
                /* */
            }
            this.incomingRingtoneAudio = null
        }
        if (this.notificationSoundAudio) {
            try {
                this.notificationSoundAudio.pause()
                this.notificationSoundAudio.src = ''
            } catch (e) {
                /* */
            }
            this.notificationSoundAudio = null
        }
        if (this.socketHandlers) {
            socket.off('connect', this.socketHandlers.onConnect)
            socket.off('disconnect', this.socketHandlers.onDisconnect)
            socket.off('user:list', this.socketHandlers.onUserList)
            socket.off('call:duration:tick', this.socketHandlers.onCallTick)
            socket.off('call:accept', this.socketHandlers.onCallAccept)
            socket.off('call:incoming', this.socketHandlers.onCallIncoming)
            socket.off('call:reject', this.socketHandlers.onCallReject)
            socket.off('call:end', this.socketHandlers.onCallEnd)
            socket.off('webrtc:offer', this.socketHandlers.onOffer)
            socket.off('webrtc:answer', this.socketHandlers.onAnswer)
            socket.off('webrtc:ice', this.socketHandlers.onIce)
            this.socketHandlers = null
        }
        this.subscribes.forEach(u => u())
    },
}
</script>

<style lang="scss" scoped>
.alert-monitor-page {
    position: relative;
}
.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.35);
    z-index: 9999;
}
.loading-icon {
    font-size: 2.5rem;
    color: #fff;
}

.monitor-sticky-header {
    position: sticky;
    top: 0;
    z-index: 100;
    background: var(--app-sidebar-bg, #1e2a3a);
    padding: 10px 0;
}
.monitor-sticky-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
}
.monitor-title {
    font-size: 1.4rem;
    font-weight: 700;
    margin: 0;
}

.alertport-kpis {
    display: flex;
    gap: 12px;
    align-items: stretch;
}
.alertport-kpi {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 6px 14px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    min-width: 110px;
}
.alertport-kpi-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.6;
}
.alertport-kpi-value {
    font-size: 1.3rem;
    font-weight: 800;
    line-height: 1.1;
}

.alertport-tabs {
    display: flex;
    gap: 4px;
    padding: 4px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
}
.alertport-tab {
    flex: 1;
    background: transparent;
    border: 0;
    color: #fff;
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}
.alertport-tab:hover {
    background: rgba(255, 255, 255, 0.05);
}
.alertport-tab.active {
    background: rgba(52, 152, 219, 0.2);
    border: 1px solid rgba(52, 152, 219, 0.5);
    color: #fff;
}
.alertport-tab-counter {
    font-size: 0.7rem;
    background: rgba(255, 255, 255, 0.15);
    padding: 2px 8px;
    border-radius: 10px;
}

/* Call actions */
.call-actions-section {
    padding: 8px 0;
}
.call-actions-grid {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}
.call-actions-grid .btn {
    min-width: 180px;
}
.call-status-text {
    font-style: italic;
}

.device-map-section {
    margin-top: 8px;
}
.device-map-wrapper {
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.08);
}
.device-map-frame {
    width: 100%;
    min-height: 220px;
    border: 0;
}

.no-events {
    text-align: center;
    padding: 40px 20px;
    color: #fff;
    opacity: 0.7;
}
.info-title {
    font-size: 1.2rem;
    font-weight: 700;
}

.time-entry-badge {
    display: inline-block;
    min-width: 150px;
    padding: 6px 10px;
    text-align: center;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1;
}

.vigilant-connection-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 0.7rem;
    font-weight: 600;
}
.vigilant-connection-badge.is-online {
    background: rgba(46, 204, 113, 0.2);
    color: #2ecc71;
}
.vigilant-connection-badge.is-offline {
    background: rgba(127, 140, 141, 0.2);
    color: #95a5a6;
}
.vigilant-connection-badge i {
    font-size: 0.55rem;
}

.list-group-selected {
    background: rgba(52, 152, 219, 0.15) !important;
    border-left: 3px solid #3498db !important;
}

.animated-dot {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
}
.dot-color {
    color: #e74c3c;
    animation: pulse 1.2s infinite;
}
@keyframes pulse {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.4;
    }
}

/* Call modal */
.call-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.65);
    z-index: 10000;
}
.call-modal-shell {
    width: 100%;
    max-width: 440px;
    padding: 0 16px;
}
.call-modal-card {
    background: var(--app-component-bg, #1d2835);
    border-radius: 20px;
    padding: 24px;
    position: relative;
    overflow: hidden;
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.4);
}
.call-modal-card.is-ringing {
    animation: pulse-ring 1.5s infinite;
}
@keyframes pulse-ring {
    0%,
    100% {
        box-shadow: 0 12px 48px rgba(0, 0, 0, 0.4);
    }
    50% {
        box-shadow: 0 12px 48px rgba(52, 152, 219, 0.4);
    }
}
.call-modal-glow {
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(52, 152, 219, 0.08) 0%, transparent 60%);
    pointer-events: none;
}
.call-modal-header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 16px;
}
.call-modal-avatar {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: rgba(52, 152, 219, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
    color: #3498db;
    flex-shrink: 0;
}
.call-modal-header-info {
    flex: 1;
}
.call-modal-overline {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.6;
    margin: 0;
}
.call-modal-name {
    font-size: 1.15rem;
    font-weight: 700;
    margin: 2px 0;
}
.call-modal-peer-type {
    font-size: 0.78rem;
    opacity: 0.5;
    margin: 0;
}
.call-modal-state {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    padding: 4px 10px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.08);
}
.call-modal-state.ringing {
    background: rgba(243, 156, 18, 0.2);
    color: #f39c12;
}
.call-modal-state.connected {
    background: rgba(46, 204, 113, 0.2);
    color: #2ecc71;
}

.call-modal-body {
    text-align: center;
    margin-bottom: 16px;
}
.call-modal-timer {
    font-size: 2rem;
    font-weight: 800;
    letter-spacing: 2px;
}
.call-modal-status {
    font-size: 0.82rem;
    opacity: 0.6;
    margin-top: 4px;
}

.call-modal-actions {
    display: flex;
    gap: 10px;
}
.call-modal-actions.active-call {
    flex-wrap: wrap;
}
.call-action-btn {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 12px 8px;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.04);
    color: #fff;
    cursor: pointer;
    font-size: 0.78rem;
    font-weight: 600;
    gap: 4px;
    transition: all 0.15s;
    min-height: 64px;
}
.call-action-btn:hover {
    background: rgba(255, 255, 255, 0.08);
}
.call-action-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}
.call-action-btn.accept {
    background: rgba(46, 204, 113, 0.2);
    border-color: #2ecc71;
    color: #2ecc71;
}
.call-action-btn.accept:hover {
    background: rgba(46, 204, 113, 0.35);
}
.call-action-btn.reject {
    background: rgba(231, 76, 60, 0.2);
    border-color: #e74c3c;
    color: #e74c3c;
}
.call-action-btn.reject:hover {
    background: rgba(231, 76, 60, 0.35);
}
.call-action-btn.end {
    background: rgba(231, 76, 60, 0.2);
    border-color: #e74c3c;
    color: #e74c3c;
}
.call-action-btn.end:hover {
    background: rgba(231, 76, 60, 0.35);
}
.call-action-btn.control.active {
    background: rgba(52, 152, 219, 0.25);
    border-color: #3498db;
    color: #3498db;
}

.call-modal-fade-enter-active,
.call-modal-fade-leave-active {
    transition: opacity 0.25s;
}
.call-modal-fade-enter,
.call-modal-fade-leave-to {
    opacity: 0;
}
</style>
